"use client"

import { shopifyImg } from "@/lib/util/img"

import type { HomeContent } from "@/lib/content/home"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { useEffect, useState } from "react"

const Hero = ({ hero, stats }: { hero: HomeContent["hero"]; stats: { products: number; brands: number } }) => {
  const features = hero.features
  const [i, setI] = useState(0)
  const [paused, setPaused] = useState(false)
  useEffect(() => {
    if (paused || features.length < 2) return
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce) return
    const t = setInterval(() => setI((x) => (x + 1) % features.length), 10000)
    return () => clearInterval(t)
  }, [paused, features.length])
  const f = features[i]

  return (
    <section className="w-full bg-elevate-black text-white" aria-labelledby="hero-title">
      <div className="content-container grid small:grid-cols-12 gap-10 py-14 small:py-20 items-center">
        <div className="small:col-span-6 medium:col-span-5 flex flex-col gap-6">
          <p className="text-xs uppercase tracking-[0.15em] text-elevate-gold">✦ {hero.eyebrow}</p>
          <h1 id="hero-title" className="font-heading text-4xl xsmall:text-5xl leading-[0.95] small:text-6xl break-words [hyphens:auto]">
            {hero.title}
          </h1>
          <p className="font-sans text-lg text-neutral-300 max-w-xl leading-relaxed">{hero.body}</p>
          <div className="flex flex-col xsmall:flex-row gap-3">
            <LocalizedClientLink href={hero.primaryCta.href} className="text-center bg-elevate-sage text-elevate-black px-6 py-3.5 text-xs uppercase tracking-[0.1em] hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-elevate-gold">
              {hero.primaryCta.label}
            </LocalizedClientLink>
            <LocalizedClientLink href={hero.secondaryCta.href} className="text-center border border-white/60 px-6 py-3.5 text-xs uppercase tracking-[0.1em] hover:bg-white hover:text-elevate-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-elevate-gold">
              {hero.secondaryCta.label} →
            </LocalizedClientLink>
          </div>
          <dl className="grid grid-cols-3 gap-4 pt-4 border-t border-white/15 max-w-md">
            <div><dt className="text-xs uppercase tracking-[0.04em] small:tracking-[0.1em] text-neutral-400">Products</dt><dd className="font-heading text-3xl">{stats.products}</dd></div>
            <div><dt className="text-xs uppercase tracking-[0.04em] small:tracking-[0.1em] text-neutral-400">Brands</dt><dd className="font-heading text-3xl">{stats.brands}</dd></div>
            <div><dt className="text-xs uppercase tracking-[0.04em] small:tracking-[0.1em] text-neutral-400">Warehouse</dt><dd className="font-heading text-3xl">MEL</dd></div>
          </dl>
        </div>
        {f && (
          <div className="small:col-span-6 medium:col-span-7 flex flex-col gap-3" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-roledescription="carousel" aria-label="Featured brands">
            <LocalizedClientLink href={f.href} className="group block">
              {/* Banners come in different shapes (2:1 brand banners, square collection art): show every one
                  whole, never cropped. A blurred copy of the same image fills any spare space. */}
              <div className="relative aspect-[2/1] w-full overflow-hidden rounded-image bg-elevate-black-deep">
                <img key={f.image + "-bg"} src={shopifyImg(f.image, 640)} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-2xl" />
                <img key={f.image} src={shopifyImg(f.image, 1100)} srcSet={`${shopifyImg(f.image, 640)} 640w, ${shopifyImg(f.image, 1100)} 1100w`} sizes="(max-width: 1024px) 100vw, 50vw" alt={f.title} fetchPriority={i === 0 ? "high" : "auto"} width={1100} height={550} className="relative h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.02]" />
              </div>
              <div className="flex items-center justify-between gap-4 pt-3 text-sm">
                <span aria-live="polite">{f.title}</span>
                <span className="shrink-0 text-xs uppercase tracking-[0.1em] text-elevate-gold">Shop brand →</span>
              </div>
            </LocalizedClientLink>
            {features.length > 1 && (
              <div className="flex items-center justify-between">
                <div className="flex gap-2" role="tablist">
                  {features.map((ft, n) => (
                    <button key={ft.title} role="tab" aria-selected={n === i} aria-label={ft.title} onClick={() => setI(n)} className={"h-1.5 w-8 " + (n === i ? "bg-elevate-gold" : "bg-white/25 hover:bg-white/50")} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button aria-label="Previous feature" onClick={() => setI((i + features.length - 1) % features.length)} className="h-9 w-9 border border-white/40 hover:bg-white hover:text-elevate-black">‹</button>
                  <button aria-label="Next feature" onClick={() => setI((i + 1) % features.length)} className="h-9 w-9 border border-white/40 hover:bg-white hover:text-elevate-black">›</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default Hero
